
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import ServicoContratante from '@/servicos/PainelAdministrativo/ServicoContratante';

export default defineComponent({
  name: 'SelecionarContratante',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Digite para selecionar',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    apresentarCpfCnpj: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();
    const refSelecionarContratante = ref<HTMLElement | null>(null);
    let debounce = 0;
    const servicoContratante = new ServicoContratante();

    const state = reactive({
      listaContratantees: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    async function obterContratantePorCodigo(valor: any) {
      if (state.emDigitacao) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else {
        if (!UtilitarioGeral.valorValido(valor)) { return; }
        if (!(valor > 0)) { return; }
        if (props.codigoSelecionado === 0) return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaContratantees)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaContratantees.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaContratantees.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaContratantees = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }
      const listaContratantees = await servicoContratante.consultaRapida(parametrosConsultaRapida, props.apresentarCpfCnpj);
      state.listaContratantees = montaOpcoesComListaConsultaRapida(listaContratantees);
    }

    async function pesquisarContratante(valorPesquisa: any) {
      state.emDigitacao = true;

      if (!UtilitarioGeral.valorValido(valorPesquisa)) { return; }
      state.buscandoDados = true;
      state.listaContratantees = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '',
        };

        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();

        const listaContratantees = await servicoContratante.consultaRapida(parametrosConsultaRapida, props.apresentarCpfCnpj);
        state.buscandoDados = false;
        state.listaContratantees = montaOpcoesComListaConsultaRapida(listaContratantees);
      }, 600);
    }

    function obterPessoa():any {
      obterContratantePorCodigo(props.codigoSelecionado);
      return props.codigoSelecionado;
    }

    const computedCodigoSelecionado = computed({
      get: () => obterPessoa(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
      },
    });

    function obterPessoas():number[] {
      obterContratantePorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterPessoas(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    function focus() {
      if (refSelecionarContratante.value) {
        refSelecionarContratante.value.focus();
      }
    }

    return {
      props,
      state,
      refSelecionarContratante,
      pesquisarContratante,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      change,
      focus,
    };
  },
});
